/**
 * Token/user persistence helpers.
 *
 * Copied near-verbatim from plugins/augurly/svelte/src/lib/authUtils.js — it is
 * entirely domain-free — with the storage keys renamed. The one behavioural
 * addition is `shouldRefresh()`, used by the API client to renew a token before
 * it expires; Augurly ships `isTokenExpired` but never calls it, so an expired
 * token there means a hard redirect in the middle of whatever you were doing.
 */

const TOKEN_KEY = 'strao_portal_token';
const USER_KEY = 'strao_portal_user';

/** Renew this many seconds before actual expiry. */
const REFRESH_MARGIN_SECONDS = 300;

export function setToken(token) {
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
}

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

export function hasToken() {
  return !!getToken();
}

/** Decode a JWT payload. Display/expiry use only — never a security check. */
export function parseToken(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const payload = parseToken(token);
  if (!payload?.exp) return false;
  return payload.exp <= Date.now() / 1000;
}

/** True when the token is still valid but close enough to expiry to renew. */
export function shouldRefresh(token) {
  const payload = parseToken(token);
  if (!payload?.exp) return false;
  const secondsLeft = payload.exp - Date.now() / 1000;
  return secondsLeft > 0 && secondsLeft < REFRESH_MARGIN_SECONDS;
}

export function isTokenValid(token) {
  return !!token && !isTokenExpired(token);
}

export function setUser(user) {
  try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch { /* ignore */ }
}

export function getUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearUser() {
  try { localStorage.removeItem(USER_KEY); } catch { /* ignore */ }
}

export function clearAuth() {
  clearToken();
  clearUser();
}

export function hasValidSession() {
  return isTokenValid(getToken()) && !!getUser();
}

export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
